import {I18n} from 'aws-amplify/utils';
import {productCopy} from '../../../config/productCopy';
import {
    Badge,
    Group,
    Loader,
    Text,
    Tooltip,
} from '@mantine/core';

import {
    Certificate,
    certificateStatusCopy,
    statusToneColor,
} from '../types';

// FNP-1193 — THE STATUS CELL, AND THE FOUR STATES IT MUST KEEP APART.
//
// ⚠️ THIS COMPONENT EXISTS BECAUSE FLATTENING THESE IS THE FAILURE THE BACKEND
// WENT OUT OF ITS WAY TO PREVENT.
//
// Three of the eight statuses describe a certificate that is not yet usable,
// and they are not variations on a theme:
//
//   PENDING_VALIDATION      progressing, inside the threshold. Wait.
//   STUCK                   the record WAS written, the provider has not
//                           confirmed it within stuckAfterMinutes. Go and
//                           compare the record against the zone.
//   VALIDATION_NOT_WRITTEN  the record was NEVER written. Nothing is
//                           propagating. Waiting achieves nothing, and this
//                           does NOT age into STUCK.
//
// Rendering all three as a spinner would discard the distinction the backend
// computes `validationRecordWrittenAt` specifically to preserve — and a silent
// pending that lasts days is indistinguishable from a broken feature, which is
// the exact complaint the ticket opens with.
//
// So: ONLY PENDING_VALIDATION GETS A SPINNER. The two attention states get a
// static badge in a colour that does not read as "in progress", because they
// are not in progress — they are waiting on a human.
export const CertificateStatusBadge = (
    {
        certificate,
        // The detail sentence is long. In a table row it belongs in a tooltip;
        // on a detail panel there is room to render it outright.
        withDetail = false,
    }: {
        certificate: Certificate,
        withDetail?: boolean,
    },
) => {

    const copy = certificateStatusCopy(certificate.status, productCopy);
    const color = statusToneColor(copy.tone);

    // ⚠️ THE SPINNER IS GATED ON `settles`, NOT ON "is it pending". Only
    // PENDING_VALIDATION sets it. STUCK and VALIDATION_NOT_WRITTEN are pending
    // at the provider and are emphatically not progressing.
    const badge = (
        <Badge
            variant={'light'}
            color={color}
            leftSection={copy.settles ? <Loader size={10} color={color}/> : undefined}
        >
            {copy.label}
        </Badge>
    );

    if (withDetail) {
        return (
            <Group gap={'xs'} wrap={'nowrap'} align={'flex-start'}>
                {badge}
                <Text size={'sm'}>{copy.detail}</Text>
            </Group>
        );
    }

    return (
        <Group gap={4} wrap={'nowrap'}>
            <Tooltip multiline={true} w={320} label={copy.detail}>
                {badge}
            </Tooltip>
            {/* The provider's own words, where it gave any. Rendered as a
                separate hover rather than folded into the sentence above,
                because one is Flexi explaining and the other is the provider
                speaking — and conflating them is how a provider's message ends
                up being read as Flexi's diagnosis. */}
            {certificate.statusDetail && (
                <Tooltip multiline={true} w={320} label={certificate.statusDetail}>
                    <Text size={'xs'} c={'dimmed'} style={{cursor: 'help'}}>
                        {I18n.get('details')}
                    </Text>
                </Tooltip>
            )}
        </Group>
    );
};
